import { ACCOUNT_STATUS_VALUES, type AccountStatus } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, Field, Icon, Input, Select, Skeleton, type BadgeTone } from "@sagot/ui";
import { useEffect, useState, type ReactNode } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { useFormatters } from "../../shared";
import { CodeSnippet, CopyTextButton, Gate, useCanManage } from "../panels/panel-kit";
import { maskCode, redeemBodyPreview, secondsUntil } from "./launch-settings-model";
import { useSandboxLaunchStatus, useStartSandboxLaunch, type LaunchSettings, type SandboxLaunch, type SandboxLaunchStatus } from "./use-launch-settings";

const K = "launch.test";
const TICK_MS = 1_000;
const STATUS_TONES: Readonly<Record<SandboxLaunchStatus["status"], BadgeTone>> = { issued: "brand", redeemed: "success", expired: "warning", revoked: "danger" };

/** Ticks once a second while `active`, for the countdown. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => { setNow(Date.now()); }, TICK_MS);
    return () => { window.clearInterval(timer); };
  }, [active]);
  return now;
}

function Step({ index, title, badge, children, testId }: { index: number; title: string; badge?: ReactNode; children: ReactNode; testId: string }) {
  return (
    <li className="flex gap-3" data-testid={testId}>
      <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-primary-ink" aria-hidden="true">
        {index}
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold">{title}</h3>
          {badge}
        </div>
        {children}
      </div>
    </li>
  );
}

function referenceOf(launch: SandboxLaunch): string {
  const request = launch.trace.find((step) => step.step === "mint")?.request as { customer?: { ref?: string } } | null | undefined;
  return request?.customer?.ref ?? "";
}

function RedeemSteps({ launch, status, slug }: { launch: SandboxLaunch; status: SandboxLaunchStatus | undefined; slug: string }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const waiting = status === undefined || status.status === "issued";
  const now = useNow(waiting);
  const left = secondsUntil(launch.expires_at, now);
  const redeem = status?.redeem ?? null;
  const session = status?.session ?? null;
  return (
    <>
      <Step index={3} title={t(`${K}.stepRedeem`)} testId="test-step-redeem" badge={status ? <Badge tone={STATUS_TONES[status.status]} dot data-testid="test-status">{t(`${K}.status_${status.status}`)}</Badge> : null}>
        <div role="status" aria-live="polite" className="space-y-2 text-sm">
          {status === undefined ? <Skeleton className="h-4 w-2/3" /> : null}
          {status?.status === "issued" ? (
            <p className="text-fg-muted" data-testid="test-waiting">
              {left > 0 ? t(`${K}.waiting`, { count: left }) : t(`${K}.waitingEnding`)}
            </p>
          ) : null}
          {status?.status === "expired" && !redeem ? <p className="text-fg-muted">{t(`${K}.expiredUnused`)}</p> : null}
          {status?.status === "revoked" ? <p className="text-fg-muted">{t(`${K}.revoked`)}</p> : null}
          {redeem ? (
            <>
              <p className="text-fg-muted" data-testid="test-redeem-at">
                {t(`${K}.redeemedAt`, { when: format.dateTime(redeem.at), origin: redeem.origin ?? t(`${K}.originUnknown`) })}
              </p>
              <div data-testid="test-redeem-body">
                <p className="mb-1 text-xs font-semibold text-fg-muted">{t(`${K}.redeemBody`)}</p>
                <CodeSnippet code={`POST /public/v1/launch/redeem\n${redeemBodyPreview(redeem.body_keys, slug)}`} label={t(`${K}.redeemBody`)} />
              </div>
            </>
          ) : null}
        </div>
      </Step>
      <Step index={4} title={t(`${K}.stepSession`)} testId="test-step-session">
        {session ? (
          <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]" data-testid="test-session">
            <dt className="text-fg-muted">{t(`${K}.sessionAccess`)}</dt>
            <dd>{format.dateTime(session.expires_at)}</dd>
            <dt className="text-fg-muted">{t(`${K}.sessionIdle`)}</dt>
            <dd>{t(`${K}.sessionIdleValue`, { count: Math.round(session.idle_timeout_s / 60) })}</dd>
            <dt className="text-fg-muted">{t(`${K}.sessionMax`)}</dt>
            <dd>{format.dateTime(session.absolute_expires_at)}</dd>
          </dl>
        ) : (
          <p className="text-sm text-fg-muted">{t(`${K}.sessionPending`)}</p>
        )}
      </Step>
    </>
  );
}

function Trace({ launch, slug }: { launch: SandboxLaunch; slug: string }) {
  const { t } = useTranslation();
  const status = useSandboxLaunchStatus(launch.launch_id);
  const mint = launch.trace.find((step) => step.step === "mint");
  const reference = referenceOf(launch);
  const clean = reference !== "" && !launch.launch_url.includes(reference);
  return (
    <ol className="space-y-4" data-testid="test-result" aria-label={t(`${K}.resultLabel`)}>
      <Step index={1} title={t(`${K}.stepMint`)} testId="test-step-mint" badge={mint?.status ? <Badge tone="success">{mint.status}</Badge> : null}>
        <p className="text-xs text-fg-muted">{t(`${K}.mintNote`)}</p>
        <CodeSnippet code={`POST /api/v1/launch-sessions\n${JSON.stringify(mint?.request ?? {}, null, 2)}`} label={t(`${K}.stepMint`)} />
      </Step>
      <Step index={2} title={t(`${K}.stepBrowser`)} testId="test-step-browser">
        <p className="text-xs text-fg-muted">{t(`${K}.browserNote`)}</p>
        <code className="block break-all rounded-sm bg-surface-2 px-2 py-1.5 font-mono text-xs" data-testid="test-launch-url">
          {maskCode(launch.launch_url)}
        </code>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="secondary">
            <a href={launch.launch_url} target="_blank" rel="noopener noreferrer" data-testid="test-open">
              <Icon name="external-link" />
              {t(`${K}.open`)}
            </a>
          </Button>
          <CopyTextButton value={launch.launch_url} label={t(`${K}.copyLink`)} className="h-8 px-3 text-xs" />
        </div>
        {clean ? (
          <p className="flex items-start gap-2 text-xs font-semibold text-success-ink" data-testid="test-clean">
            <Icon name="shield-check" className="mt-0.5 size-3.5 shrink-0" />
            {t(`${K}.nothingPersonal`)}
          </p>
        ) : null}
      </Step>
      <RedeemSteps launch={launch} status={status.data} slug={slug} />
      {status.isError ? (
        <li className="list-none">
          <p className="text-xs font-semibold text-danger" role="alert">
            {t(`${K}.statusError`)}
          </p>
        </li>
      ) : null}
    </ol>
  );
}

/**
 * Secure launch "Test launch" card (mockup secure-launch pin 6, PID-004): mints a sandbox code for a made-up customer and shows
 * what crossed the browser, step by step: the mint call, the launch address (code after `#` only), the redeem body the hosted
 * chat sends and the session that opens. The page polls until the code is used or expires.
 */
export function TestLaunchCard({ settings }: { settings: LaunchSettings }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const manage = useCanManage("customers.manage_launch");
  const { summary } = useWorkspaceContext();
  const start = useStartSandboxLaunch();
  const liveAgents = settings.agents.filter((agent) => agent.live);
  const [agentId, setAgentId] = useState("");
  const [tier, setTier] = useState("");
  const [status, setStatus] = useState<AccountStatus>("active");
  const [name, setName] = useState("");
  const [launch, setLaunch] = useState<SandboxLaunch | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const chosen = liveAgents.find((agent) => agent.agent_id === agentId)?.agent_id ?? liveAgents[0]?.agent_id ?? "";
  // Secure launch Lite takes the reference and display name only: a rehearsal that sends facts would fail where production does.
  const facts = settings.plan.facts_included;
  const run = async () => {
    setFailure(null);
    try {
      const made = await start.mutateAsync({
        agent_id: chosen,
        customer: { ...(facts ? { account_status: status, ...(tier ? { tier } : {}) } : {}), ...(name.trim() ? { display_name: name.trim() } : {}) },
      });
      setLaunch(made);
    } catch (caught) {
      setFailure(message(caught));
    }
  };
  return (
    <section className="card" aria-labelledby="test-launch-title" data-testid="test-launch-card">
      <div className="card-header">
        <div className="min-w-0">
          <h2 id="test-launch-title" className="card-title">
            {t(`${K}.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.subtitle`)}</p>
        </div>
      </div>
      {liveAgents.length === 0 ? (
        <EmptyState icon="bot" headingLevel={3} title={t(`${K}.noLiveTitle`)} description={t(`${K}.noLiveBody`)} />
      ) : (
        <div className="space-y-5 px-5 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t(`${K}.agent`)}>
              <Select value={chosen} onChange={(event) => { setAgentId(event.target.value); }} disabled={!manage.allowed} data-testid="test-agent">
                {liveAgents.map((agent) => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                    {agent.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t(`${K}.tier`)} hint={!facts ? t(`${K}.plusOnly`) : settings.customer_tiers.length === 0 ? t(`${K}.noTiers`) : undefined}>
              <Select value={tier} onChange={(event) => { setTier(event.target.value); }} disabled={!manage.allowed || !facts} data-testid="test-tier">
                <option value="">{t(`${K}.noTier`)}</option>
                {settings.customer_tiers.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.label.en ?? entry.code}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t(`${K}.status`)} hint={!facts ? t(`${K}.plusOnly`) : undefined}>
              <Select value={status} onChange={(event) => { setStatus(event.target.value as AccountStatus); }} disabled={!manage.allowed || !facts} data-testid="test-status-select">
                {ACCOUNT_STATUS_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {t(`${K}.statuses.${value}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t(`${K}.displayName`)}>
              <Input value={name} maxLength={60} placeholder={t(`${K}.displayNamePlaceholder`)} onChange={(event) => { setName(event.target.value); }} disabled={!manage.allowed} data-testid="test-name" />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Gate reason={manage.reason}>
              <Button loading={start.isPending} disabled={!manage.allowed || chosen === ""} onClick={() => void run()} data-testid="test-run">
                <Icon name="play" />
                {launch ? t(`${K}.runAgain`) : t(`${K}.run`)}
              </Button>
            </Gate>
            <p className="text-xs text-fg-muted">{t(`${K}.sandboxNote`)}</p>
          </div>
          {failure ? (
            <p role="alert" className="text-xs font-semibold text-danger" data-testid="test-error">
              {failure}
            </p>
          ) : null}
          {launch ? <Trace key={launch.launch_id} launch={launch} slug={summary.slug} /> : null}
        </div>
      )}
    </section>
  );
}
